import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tag, Plus, Pencil, Trash2, Calculator, Package2, Percent, Layers, Globe, Link2, Copy, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { FasceOrarie } from "@/pages/FasceOrarie";

const eur = (v) => `€ ${Number(v || 0).toFixed(2)}`;

function DelBtn({ onConfirm, testid, label }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid={testid} className="p-1.5 text-zinc-500 hover:text-[#FF3B30] hover:bg-zinc-800 rounded-sm"><Trash2 className="h-4 w-4" /></button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#141414] border-zinc-800">
        <AlertDialogHeader><AlertDialogTitle>Eliminare {label}?</AlertDialogTitle><AlertDialogDescription>Azione irreversibile.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel><AlertDialogAction onClick={onConfirm} className="rounded-sm bg-[#FF3B30] hover:bg-[#cc2f26]">Elimina</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const TAR_EMPTY = { nome: "", categoria_id: "", modello: "", prezzo_mezza_giornata: 0, prezzo_giornata: 0, prezzo_giorno_aggiuntivo: 0, prezzo_settimana: 0, cauzione: 0, data_inizio_validita: "", data_fine_validita: "", attivo: true };
const ACC_EMPTY = { nome: "", prezzo: 0, quantita_disponibile: 0, attivo: true };
const SCO_EMPTY = { codice: "", descrizione: "", tipo: "percentuale", percentuale: 0, importo_fisso: 0, min_giorni: 0, min_importo: 0, data_inizio: "", data_fine: "", max_utilizzi: 0, max_per_cliente: 0, categorie_ids: [], automatico: false, attivo: true };

export default function Pricing() {
  const { hasPerm } = useAuth();
  const [categories, setCategories] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [discounts, setDiscounts] = useState([]);

  const canCreate = hasPerm("pricing.create");
  const canEdit = hasPerm("pricing.edit");
  const canDelete = hasPerm("pricing.delete");

  const load = async () => {
    try { setTariffs((await api.get("/pricing/tariffs")).data); } catch (e) {}
    try { setAccessories((await api.get("/pricing/accessories")).data); } catch (e) {}
    try { setDiscounts((await api.get("/pricing/discounts")).data); } catch (e) {}
    try { setCategories((await api.get("/categories")).data); } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  // ---- Tariff dialog ----
  const [tOpen, setTOpen] = useState(false);
  const [tForm, setTForm] = useState(TAR_EMPTY);
  const [tEditing, setTEditing] = useState(null);
  const openTariff = (t) => { setTEditing(t || null); setTForm(t ? { ...TAR_EMPTY, ...t, data_inizio_validita: t.data_inizio_validita || "", data_fine_validita: t.data_fine_validita || "" } : TAR_EMPTY); setTOpen(true); };
  const saveTariff = async () => {
    if (!tForm.nome.trim() || !tForm.categoria_id) { toast.error("Nome e categoria obbligatori"); return; }
    const p = { ...tForm,
      prezzo_mezza_giornata: +tForm.prezzo_mezza_giornata || 0, prezzo_giornata: +tForm.prezzo_giornata || 0,
      prezzo_giorno_aggiuntivo: +tForm.prezzo_giorno_aggiuntivo || 0, prezzo_settimana: +tForm.prezzo_settimana || 0,
      cauzione: +tForm.cauzione || 0,
      data_inizio_validita: tForm.data_inizio_validita || null, data_fine_validita: tForm.data_fine_validita || null };
    try {
      if (tEditing) await api.put(`/pricing/tariffs/${tEditing.id}`, p);
      else await api.post("/pricing/tariffs", p);
      toast.success("Tariffa salvata"); setTOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  // ---- Accessory dialog ----
  const [aOpen, setAOpen] = useState(false);
  const [aForm, setAForm] = useState(ACC_EMPTY);
  const [aEditing, setAEditing] = useState(null);
  const openAcc = (a) => { setAEditing(a || null); setAForm(a ? { ...ACC_EMPTY, ...a } : ACC_EMPTY); setAOpen(true); };
  const saveAcc = async () => {
    if (!aForm.nome.trim()) { toast.error("Nome obbligatorio"); return; }
    const p = { ...aForm, prezzo: +aForm.prezzo || 0, quantita_disponibile: parseInt(aForm.quantita_disponibile) || 0 };
    try {
      if (aEditing) await api.put(`/pricing/accessories/${aEditing.id}`, p);
      else await api.post("/pricing/accessories", p);
      toast.success("Accessorio salvato"); setAOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  // ---- Discount dialog ----
  const [dOpen, setDOpen] = useState(false);
  const [dForm, setDForm] = useState(SCO_EMPTY);
  const [dEditing, setDEditing] = useState(null);
  const openDisc = (d) => { setDEditing(d || null); setDForm(d ? { ...SCO_EMPTY, ...d, data_inizio: d.data_inizio || "", data_fine: d.data_fine || "", categorie_ids: d.categorie_ids || [] } : SCO_EMPTY); setDOpen(true); };
  const saveDisc = async () => {
    if (!dForm.automatico && !dForm.codice.trim()) { toast.error("Inserisci un codice o imposta 'automatico'"); return; }
    const p = { ...dForm,
      percentuale: +dForm.percentuale || 0, importo_fisso: +dForm.importo_fisso || 0,
      min_giorni: parseInt(dForm.min_giorni) || 0, min_importo: +dForm.min_importo || 0,
      max_utilizzi: parseInt(dForm.max_utilizzi) || 0, max_per_cliente: parseInt(dForm.max_per_cliente) || 0,
      data_inizio: dForm.data_inizio || null, data_fine: dForm.data_fine || null,
      categorie_ids: dForm.categorie_ids || [] };
    try {
      if (dEditing) await api.put(`/pricing/discounts/${dEditing.id}`, p);
      else await api.post("/pricing/discounts", p);
      toast.success("Sconto salvato"); setDOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const toggleDiscCat = (id) => setDForm((f) => ({ ...f, categorie_ids: (f.categorie_ids || []).includes(id) ? f.categorie_ids.filter((x) => x !== id) : [...(f.categorie_ids || []), id] }));

  const catName = (id) => categories.find((c) => c.id === id)?.nome || "—";

  return (
    <div className="p-5 sm:p-8 max-w-6xl mx-auto fade-in">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Tariffe di noleggio</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">LISTINI</h1>
      </div>

      <Tabs defaultValue="tariffe">
        <TabsList className="bg-[#141414] border border-zinc-800 rounded-sm mb-6 flex-wrap h-auto">
          <TabsTrigger value="tariffe" data-testid="tab-tariffe" className="data-[state=active]:bg-[#f98125] data-[state=active]:text-white rounded-sm"><Tag className="h-4 w-4 mr-1.5" /> Tariffe</TabsTrigger>
          <TabsTrigger value="accessori" data-testid="tab-accessori" className="data-[state=active]:bg-[#f98125] data-[state=active]:text-white rounded-sm"><Package2 className="h-4 w-4 mr-1.5" /> Accessori</TabsTrigger>
          <TabsTrigger value="sconti" data-testid="tab-sconti" className="data-[state=active]:bg-[#f98125] data-[state=active]:text-white rounded-sm"><Percent className="h-4 w-4 mr-1.5" /> Sconti</TabsTrigger>
          <TabsTrigger value="fasce" data-testid="tab-fasce" className="data-[state=active]:bg-[#f98125] data-[state=active]:text-white rounded-sm"><Clock className="h-4 w-4 mr-1.5" /> Fasce orarie</TabsTrigger>
          <TabsTrigger value="preventivo" data-testid="tab-preventivo" className="data-[state=active]:bg-[#f98125] data-[state=active]:text-white rounded-sm"><Calculator className="h-4 w-4 mr-1.5" /> Preventivo</TabsTrigger>
          <TabsTrigger value="online" data-testid="tab-online" className="data-[state=active]:bg-[#f98125] data-[state=active]:text-white rounded-sm"><Globe className="h-4 w-4 mr-1.5" /> Prenotazioni Online</TabsTrigger>
        </TabsList>

        {/* TARIFFE */}
        <TabsContent value="tariffe">
          {canCreate && <button onClick={() => openTariff(null)} data-testid="add-tariff-btn" className="mb-4 flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold uppercase tracking-wide px-4 py-2.5 text-sm hover:bg-[#d96a12] transition-colors"><Plus className="h-4 w-4" /> Nuova tariffa</button>}
          {tariffs.length === 0 ? (
            <div className="text-zinc-600 text-sm py-12 text-center border border-dashed border-zinc-800 rounded-sm">Nessuna tariffa. {categories.length === 0 && "Crea prima delle categorie bici."}</div>
          ) : (
            <div className="space-y-2">
              {tariffs.map((t) => (
                <div key={t.id} data-testid={`tariff-row-${t.id}`} className="bg-[#141414] border border-zinc-800 rounded-sm p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-semibold flex items-center gap-2">{t.nome}
                        <span className="text-[10px] uppercase tracking-wider bg-[#f98125]/15 text-[#f98125] px-2 py-0.5 rounded-sm flex items-center gap-1"><Layers className="h-3 w-3" />{t.categoria_nome || catName(t.categoria_id)}</span>
                        {t.modello && <span className="text-[10px] uppercase tracking-wider bg-zinc-700/40 text-zinc-300 px-2 py-0.5 rounded-sm">modello: {t.modello}</span>}
                        {!t.attivo && <span className="text-[10px] uppercase tracking-wider text-zinc-600">non attiva</span>}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">
                        {t.data_inizio_validita || t.data_fine_validita ? `Valida ${t.data_inizio_validita || "…"} → ${t.data_fine_validita || "…"}` : "Valida sempre"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {canEdit && <button onClick={() => openTariff(t)} data-testid={`edit-tariff-${t.id}`} className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-sm"><Pencil className="h-4 w-4" /></button>}
                      {canDelete && <DelBtn testid={`delete-tariff-${t.id}`} label={`"${t.nome}"`} onConfirm={async () => { await api.delete(`/pricing/tariffs/${t.id}`); toast.success("Eliminata"); load(); }} />}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3 pt-3 border-t border-zinc-800/60 text-sm">
                    <Price label="Mezza giornata" v={t.prezzo_mezza_giornata} />
                    <Price label="Giornata" v={t.prezzo_giornata} />
                    <Price label="Giorno agg." v={t.prezzo_giorno_aggiuntivo} />
                    <Price label="Settimana" v={t.prezzo_settimana} />
                    <Price label="Cauzione" v={t.cauzione} accent />
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ACCESSORI */}
        <TabsContent value="accessori">
          {canCreate && <button onClick={() => openAcc(null)} data-testid="add-accessory-btn" className="mb-4 flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold uppercase tracking-wide px-4 py-2.5 text-sm hover:bg-[#d96a12] transition-colors"><Plus className="h-4 w-4" /> Nuovo accessorio</button>}
          {accessories.length === 0 ? (
            <div className="text-zinc-600 text-sm py-12 text-center border border-dashed border-zinc-800 rounded-sm">Nessun accessorio (es. casco, seggiolino, kit riparazione).</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {accessories.map((a) => (
                <div key={a.id} data-testid={`accessory-row-${a.id}`} className="bg-[#141414] border border-zinc-800 rounded-sm p-4 flex items-center justify-between">
                  <div>
                    <div className="font-medium">{a.nome} {!a.attivo && <span className="text-[10px] uppercase text-zinc-600">(off)</span>}</div>
                    <div className="text-xs text-zinc-500">{eur(a.prezzo)} · disp. {a.quantita_disponibile}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    {canEdit && <button onClick={() => openAcc(a)} data-testid={`edit-accessory-${a.id}`} className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-sm"><Pencil className="h-4 w-4" /></button>}
                    {canDelete && <DelBtn testid={`delete-accessory-${a.id}`} label={`"${a.nome}"`} onConfirm={async () => { await api.delete(`/pricing/accessories/${a.id}`); toast.success("Eliminato"); load(); }} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* SCONTI */}
        <TabsContent value="sconti">
          {canCreate && <button onClick={() => openDisc(null)} data-testid="add-discount-btn" className="mb-4 flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold uppercase tracking-wide px-4 py-2.5 text-sm hover:bg-[#d96a12] transition-colors"><Plus className="h-4 w-4" /> Nuovo sconto</button>}
          {discounts.length === 0 ? (
            <div className="text-zinc-600 text-sm py-12 text-center border border-dashed border-zinc-800 rounded-sm">Nessuno sconto configurato.</div>
          ) : (
            <div className="space-y-2">
              {discounts.map((d) => (
                <div key={d.id} data-testid={`discount-row-${d.id}`} className="bg-[#141414] border border-zinc-800 rounded-sm p-4 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-medium flex items-center gap-2 flex-wrap">
                      <span className="font-display text-2xl text-[#f98125]">{d.tipo === "fisso" ? eur(d.importo_fisso) : `${d.percentuale}%`}</span>
                      {d.codice ? <span className="text-xs bg-zinc-800 px-2 py-0.5 rounded-sm font-mono">{d.codice}</span> : <span className="text-[10px] uppercase tracking-wider text-[#39FF14]">automatico</span>}
                      {!d.attivo && <span className="text-[10px] uppercase text-zinc-600">(off)</span>}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">{d.descrizione}</div>
                    <div className="text-[11px] text-zinc-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      {d.min_giorni > 0 && <span>da {d.min_giorni}gg</span>}
                      {d.min_importo > 0 && <span>min {eur(d.min_importo)}</span>}
                      {(d.data_inizio || d.data_fine) && <span>{d.data_inizio || "…"} → {d.data_fine || "…"}</span>}
                      {d.max_utilizzi > 0 && <span>usi {d.utilizzi_totali ?? 0}/{d.max_utilizzi}</span>}
                      {d.max_utilizzi <= 0 && (d.utilizzi_totali ?? 0) > 0 && <span>usi {d.utilizzi_totali}</span>}
                      {d.max_per_cliente > 0 && <span>max {d.max_per_cliente}/cliente</span>}
                      {(d.categorie_ids || []).length > 0 && <span>{d.categorie_ids.length} categorie</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {canEdit && <button onClick={() => openDisc(d)} data-testid={`edit-discount-${d.id}`} className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-sm"><Pencil className="h-4 w-4" /></button>}
                    {canDelete && <DelBtn testid={`delete-discount-${d.id}`} label="lo sconto" onConfirm={async () => { await api.delete(`/pricing/discounts/${d.id}`); toast.success("Eliminato"); load(); }} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* PREVENTIVO */}
        <TabsContent value="preventivo">
          <QuoteCalculator categories={categories} accessories={accessories} />
        </TabsContent>

        {/* FASCE ORARIE */}
        <TabsContent value="fasce">
          <FasceOrarie />
        </TabsContent>

        {/* PRENOTAZIONI ONLINE */}
        <TabsContent value="online">
          <BookingSettings canEdit={canEdit} />
        </TabsContent>
      </Tabs>

      {/* Tariff dialog */}
      <Dialog open={tOpen} onOpenChange={setTOpen}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><Tag className="h-5 w-5 text-[#f98125]" /> {tEditing ? "Modifica tariffa" : "Nuova tariffa"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Nome *</Label><Input data-testid="tariff-nome" value={tForm.nome} onChange={(e) => setTForm({ ...tForm, nome: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="es. Alta stagione" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Categoria *</Label>
                <Select value={tForm.categoria_id} onValueChange={(v) => setTForm({ ...tForm, categoria_id: v })}>
                  <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="tariff-categoria"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-zinc-800">{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <NumField label="Mezza giornata €" testid="tariff-mezza" value={tForm.prezzo_mezza_giornata} onChange={(v) => setTForm({ ...tForm, prezzo_mezza_giornata: v })} />
              <NumField label="Giornata €" testid="tariff-giornata" value={tForm.prezzo_giornata} onChange={(v) => setTForm({ ...tForm, prezzo_giornata: v })} />
              <NumField label="Giorno agg. €" testid="tariff-agg" value={tForm.prezzo_giorno_aggiuntivo} onChange={(v) => setTForm({ ...tForm, prezzo_giorno_aggiuntivo: v })} />
              <NumField label="Settimana €" testid="tariff-settimana" value={tForm.prezzo_settimana} onChange={(v) => setTForm({ ...tForm, prezzo_settimana: v })} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <NumField label="Cauzione €" testid="tariff-cauzione" value={tForm.cauzione} onChange={(v) => setTForm({ ...tForm, cauzione: v })} />
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Valida dal</Label><Input type="date" data-testid="tariff-dal" value={tForm.data_inizio_validita} onChange={(e) => setTForm({ ...tForm, data_inizio_validita: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Valida al</Label><Input type="date" data-testid="tariff-al" value={tForm.data_fine_validita} onChange={(e) => setTForm({ ...tForm, data_fine_validita: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 items-center">
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Override modello (opzionale)</Label><Input data-testid="tariff-modello" value={tForm.modello} onChange={(e) => setTForm({ ...tForm, modello: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="lascia vuoto per categoria" /></div>
              <label className="flex items-center gap-2 cursor-pointer mt-5"><Switch checked={tForm.attivo} onCheckedChange={(v) => setTForm({ ...tForm, attivo: v })} data-testid="tariff-attivo" /> <span className="text-sm">Tariffa attiva</span></label>
            </div>
            <p className="text-xs text-zinc-600">Se più tariffe si sovrappongono nelle date, vince la più specifica (override modello, poi finestra date più stretta).</p>
          </div>
          <DialogFooter>
            <button onClick={() => setTOpen(false)} className="rounded-sm border border-zinc-700 px-5 py-2.5 hover:bg-zinc-800 transition-colors">Annulla</button>
            <button onClick={saveTariff} data-testid="tariff-save" className="rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 hover:bg-[#d96a12] transition-colors">Salva</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accessory dialog */}
      <Dialog open={aOpen} onOpenChange={setAOpen}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><Package2 className="h-5 w-5 text-[#f98125]" /> {aEditing ? "Modifica accessorio" : "Nuovo accessorio"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Nome *</Label><Input data-testid="acc-nome" value={aForm.nome} onChange={(e) => setAForm({ ...aForm, nome: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="es. Casco" /></div>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Prezzo €" testid="acc-prezzo" value={aForm.prezzo} onChange={(v) => setAForm({ ...aForm, prezzo: v })} />
              <NumField label="Quantità disp." testid="acc-qta" value={aForm.quantita_disponibile} onChange={(v) => setAForm({ ...aForm, quantita_disponibile: v })} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer"><Switch checked={aForm.attivo} onCheckedChange={(v) => setAForm({ ...aForm, attivo: v })} data-testid="acc-attivo" /> <span className="text-sm">Disponibile a noleggio</span></label>
          </div>
          <DialogFooter>
            <button onClick={() => setAOpen(false)} className="rounded-sm border border-zinc-700 px-5 py-2.5 hover:bg-zinc-800 transition-colors">Annulla</button>
            <button onClick={saveAcc} data-testid="acc-save" className="rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 hover:bg-[#d96a12] transition-colors">Salva</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discount dialog */}
      <Dialog open={dOpen} onOpenChange={setDOpen}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><Percent className="h-5 w-5 text-[#f98125]" /> {dEditing ? "Modifica sconto" : "Nuovo sconto"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Codice</Label><Input data-testid="disc-codice" value={dForm.codice} onChange={(e) => setDForm({ ...dForm, codice: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm font-mono" placeholder="ESTATE10" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Tipo</Label>
                <Select value={dForm.tipo} onValueChange={(v) => setDForm({ ...dForm, tipo: v })}>
                  <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="disc-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-zinc-800">
                    <SelectItem value="percentuale">Percentuale %</SelectItem>
                    <SelectItem value="fisso">Importo fisso €</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {dForm.tipo === "fisso"
                ? <NumField label="Importo sconto €" testid="disc-fisso" value={dForm.importo_fisso} onChange={(v) => setDForm({ ...dForm, importo_fisso: v })} />
                : <NumField label="Percentuale %" testid="disc-perc" value={dForm.percentuale} onChange={(v) => setDForm({ ...dForm, percentuale: v })} />}
              <NumField label="Importo minimo noleggio €" testid="disc-minimporto" value={dForm.min_importo} onChange={(v) => setDForm({ ...dForm, min_importo: v })} />
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Descrizione</Label><Input data-testid="disc-desc" value={dForm.descrizione} onChange={(e) => setDForm({ ...dForm, descrizione: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Valido dal</Label><Input type="date" data-testid="disc-dal" value={dForm.data_inizio} onChange={(e) => setDForm({ ...dForm, data_inizio: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Valido al</Label><Input type="date" data-testid="disc-al" value={dForm.data_fine} onChange={(e) => setDForm({ ...dForm, data_fine: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <NumField label="Min. giorni" testid="disc-mingiorni" value={dForm.min_giorni} onChange={(v) => setDForm({ ...dForm, min_giorni: v })} />
              <NumField label="Max utilizzi (0=∞)" testid="disc-maxutilizzi" value={dForm.max_utilizzi} onChange={(v) => setDForm({ ...dForm, max_utilizzi: v })} />
              <NumField label="Max per cliente" testid="disc-maxcliente" value={dForm.max_per_cliente} onChange={(v) => setDForm({ ...dForm, max_per_cliente: v })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Categorie ammesse (vuoto = tutte)</Label>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((c) => (
                  <label key={c.id} data-testid={`disc-cat-${c.id}`} className={`flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer text-sm ${(dForm.categorie_ids || []).includes(c.id) ? "border-[#f98125] bg-[#f98125]/10" : "border-zinc-800"}`}>
                    <Checkbox checked={(dForm.categorie_ids || []).includes(c.id)} onCheckedChange={() => toggleDiscCat(c.id)} />
                    {c.nome}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-center">
              <label className="flex items-center gap-2 cursor-pointer"><Switch checked={dForm.automatico} onCheckedChange={(v) => setDForm({ ...dForm, automatico: v })} data-testid="disc-auto" /> <span className="text-sm">Automatico</span></label>
              <label className="flex items-center gap-2 cursor-pointer"><Switch checked={dForm.attivo} onCheckedChange={(v) => setDForm({ ...dForm, attivo: v })} data-testid="disc-attivo" /> <span className="text-sm">Attivo</span></label>
            </div>
            <p className="text-xs text-zinc-600">"Automatico" si applica da solo al raggiungimento dei requisiti; altrimenti serve il codice in fase di preventivo/prenotazione.</p>
          </div>
          <DialogFooter>
            <button onClick={() => setDOpen(false)} className="rounded-sm border border-zinc-700 px-5 py-2.5 hover:bg-zinc-800 transition-colors">Annulla</button>
            <button onClick={saveDisc} data-testid="disc-save" className="rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 hover:bg-[#d96a12] transition-colors">Salva</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Price({ label, v, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-600">{label}</div>
      <div className={`font-medium ${accent ? "text-[#FF9500]" : "text-white"}`}>{eur(v)}</div>
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-zinc-500">{label}</Label>
      <Input type="number" step="0.01" data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
    </div>
  );
}

function BookingSettings({ canEdit }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const publicUrl = `${window.location.origin}/prenota`;

  useEffect(() => { api.get("/settings/public-booking").then((r) => setS(r.data)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/public-booking", {
        deposit_percent: +s.deposit_percent || 0,
        cancellation_policy: s.cancellation_policy || "",
        booking_enabled: !!s.booking_enabled,
      });
      setS(r.data); toast.success("Impostazioni salvate");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="text-zinc-600 text-sm py-12 text-center">Caricamento…</div>;

  return (
    <div className="max-w-2xl space-y-5">
      <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm">
          <Link2 className="h-4 w-4 text-[#f98125]" />
          <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="public-link" className="text-[#f98125] hover:underline font-mono break-all">{publicUrl}</a>
        </div>
        <button data-testid="copy-public-link" onClick={() => { navigator.clipboard?.writeText(publicUrl); toast.success("Link copiato"); }} className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm"><Copy className="h-4 w-4" /></button>
      </div>

      <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5 space-y-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <Switch checked={s.booking_enabled} onCheckedChange={(v) => setS({ ...s, booking_enabled: v })} data-testid="booking-enabled" disabled={!canEdit} />
          <span className="text-sm font-medium">Prenotazioni online attive</span>
        </label>
        <div className="max-w-[220px]">
          <Label className="text-xs uppercase tracking-wider text-zinc-500">Acconto richiesto (%)</Label>
          <Input type="number" step="1" min="0" max="100" data-testid="deposit-percent" value={s.deposit_percent} onChange={(e) => setS({ ...s, deposit_percent: e.target.value })} disabled={!canEdit} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">Policy di cancellazione (mostrata al cliente)</Label>
          <textarea data-testid="cancellation-policy" value={s.cancellation_policy} onChange={(e) => setS({ ...s, cancellation_policy: e.target.value })} disabled={!canEdit} rows={5} className="mt-1.5 w-full bg-[#0A0A0A] border border-zinc-800 rounded-sm p-3 text-sm outline-none focus:border-[#f98125]" />
        </div>
        {canEdit && <button onClick={save} disabled={saving} data-testid="save-booking-settings" className="rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 hover:bg-[#d96a12] transition-colors disabled:opacity-60">{saving ? "Salvataggio…" : "Salva impostazioni"}</button>}
      </div>
      <p className="text-xs text-zinc-600">L'acconto viene incassato online tramite Stripe (modalità test). Saldo e cauzione si pagano in sede. I rimborsi si gestiscono dalla scheda del singolo noleggio.</p>
    </div>
  );
}

function QuoteCalculator({ categories, accessories }) {
  const now = new Date();
  const tomorrow = new Date(now.getTime() + 24 * 3600 * 1000);
  const fmt = (d) => d.toISOString().slice(0, 16);
  const [categoria, setCategoria] = useState("");
  const [inizio, setInizio] = useState(fmt(now));
  const [fine, setFine] = useState(fmt(tomorrow));
  const [accSel, setAccSel] = useState([]);
  const [codice, setCodice] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggleAcc = (id) => setAccSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const calcola = async () => {
    if (!categoria) { toast.error("Seleziona una categoria"); return; }
    setLoading(true); setError(null); setResult(null);
    try {
      const res = await api.post("/pricing/quote", {
        categoria_id: categoria,
        inizio: new Date(inizio).toISOString(),
        fine: new Date(fine).toISOString(),
        accessori: accSel,
        codice_sconto: codice,
      });
      setResult(res.data);
    } catch (e) { setError(e.response?.data?.detail || "Errore nel calcolo"); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5 space-y-4">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Parametri preventivo</div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">Categoria *</Label>
          <Select value={categoria} onValueChange={setCategoria}>
            <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="quote-categoria"><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-zinc-800">{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Inizio</Label><Input type="datetime-local" data-testid="quote-inizio" value={inizio} onChange={(e) => setInizio(e.target.value)} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Fine</Label><Input type="datetime-local" data-testid="quote-fine" value={fine} onChange={(e) => setFine(e.target.value)} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
        </div>
        {accessories.filter((a) => a.attivo).length > 0 && (
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Accessori</Label>
            <div className="grid sm:grid-cols-2 gap-2">
              {accessories.filter((a) => a.attivo).map((a) => (
                <label key={a.id} data-testid={`quote-acc-${a.id}`} className={`flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer text-sm ${accSel.includes(a.id) ? "border-[#f98125] bg-[#f98125]/10" : "border-zinc-800"}`}>
                  <Checkbox checked={accSel.includes(a.id)} onCheckedChange={() => toggleAcc(a.id)} />
                  {a.nome} <span className="text-zinc-500 ml-auto">{eur(a.prezzo)}</span>
                </label>
              ))}
            </div>
          </div>
        )}
        <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Codice sconto</Label><Input data-testid="quote-codice" value={codice} onChange={(e) => setCodice(e.target.value)} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm font-mono" placeholder="opzionale" /></div>
        <button onClick={calcola} disabled={loading} data-testid="quote-calc-btn" className="w-full flex items-center justify-center gap-2 rounded-sm bg-[#f98125] text-white font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#d96a12] transition-colors disabled:opacity-50"><Calculator className="h-5 w-5" /> {loading ? "Calcolo..." : "Calcola preventivo"}</button>
      </div>

      <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">Dettaglio prezzo</div>
        {error && <div className="text-[#FF3B30] text-sm" data-testid="quote-error">{error}</div>}
        {!result && !error && <div className="text-zinc-600 text-sm py-10 text-center">Compila i parametri e calcola.</div>}
        {result && (
          <div className="space-y-2 text-sm" data-testid="quote-result">
            <div className="flex justify-between text-zinc-400"><span>Tariffa applicata</span><span className="text-white">{result.tariffa_nome}</span></div>
            <div className="flex justify-between text-zinc-400"><span>Durata</span><span className="text-white">{result.tipo_durata === "mezza_giornata" ? "Mezza giornata" : `${result.giorni} giorni`} ({result.ore} h)</span></div>
            <div className="border-t border-zinc-800 my-2" />
            {result.dettaglio.map((r, i) => (
              <div key={i} className="flex justify-between text-zinc-400"><span>{r.voce} ×{r.qta}</span><span className="text-white">{eur(r.importo)}</span></div>
            ))}
            {result.accessori.map((a, i) => (
              <div key={i} className="flex justify-between text-zinc-400"><span>Accessorio: {a.nome}{a.qta > 1 ? ` ×${a.qta}` : ""}</span><span className="text-white">{eur(a.importo != null ? a.importo : a.prezzo)}</span></div>
            ))}
            <div className="border-t border-zinc-800 my-2" />
            <div className="flex justify-between text-zinc-400"><span>Imponibile</span><span className="text-white">{eur(result.imponibile)}</span></div>
            {result.sconto && (
              <div className="flex justify-between text-[#39FF14]"><span>Sconto {result.sconto.tipo === "fisso" ? eur(result.sconto.importo_fisso) : `${result.sconto.percentuale}%`} {result.sconto.codice && `(${result.sconto.codice})`}</span><span>− {eur(result.sconto_importo)}</span></div>
            )}
            {result.sconto_errore && (
              <div className="text-[#FF9500] text-xs" data-testid="quote-sconto-errore">⚠ {result.sconto_errore}</div>
            )}
            <div className="flex justify-between items-center pt-2 border-t border-zinc-800">
              <span className="font-display text-xl">TOTALE</span>
              <span className="font-display text-3xl text-[#f98125]" data-testid="quote-totale">{eur(result.totale)}</span>
            </div>
            <div className="flex justify-between text-zinc-500 text-xs pt-1"><span>Cauzione (rimborsabile)</span><span>{eur(result.cauzione)}</span></div>
            <div className="flex justify-between text-zinc-400 text-xs"><span>Totale + cauzione al ritiro</span><span>{eur(result.totale_con_cauzione)}</span></div>
          </div>
        )}
      </div>
    </div>
  );
}
